// ProductDropdown component - Styled dropdown for product selection
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { loadCanonicalProducts } from '../utils/productMatcher';

const ProductDropdown = ({ value, onChange, onSearch, isLoading, selectedCanonicalNames = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [canonicalProducts, setCanonicalProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState({});
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Sync selectedProducts with prop changes (for deep linking)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Syncing dropdown with selectedCanonicalNames:', selectedCanonicalNames);
    }
    
    if (selectedCanonicalNames && selectedCanonicalNames.length > 0 && canonicalProducts && Object.keys(canonicalProducts).length > 0) {
      const matchedProducts = [];
      
      // Find products that match the canonical names
      Object.values(canonicalProducts).flat().forEach(product => {
        if (selectedCanonicalNames.includes(product.canonicalName)) {
          matchedProducts.push(product);
        }
      });
      if (process.env.NODE_ENV === 'development') { 
        console.log('Matched products for dropdown:', matchedProducts);
      }
      setSelectedProducts(matchedProducts);
    } else if (!selectedCanonicalNames || selectedCanonicalNames.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Clearing dropdown selection');
      }
      setSelectedProducts([]);
    }
  }, [selectedCanonicalNames, canonicalProducts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load canonical products from JSON
  useEffect(() => {
    const fetchAndGroupProducts = async () => {
      try {
        // Use the shared loader — correct CDN URL, shared cache, no duplicate fetches
        const products = await loadCanonicalProducts();
        const data = { products };
        
        // Group products by category
        const grouped = {
          'Good Vibes Pocket 150mg': [],
          'Good Vibes 500mg': [],
          'Good Vibes Rosin': [],
          'Good Vibes X 1000mg': [],
          'Good Vibes Plus': [],
          'Good Feels 150mg': []
        };

        data.products.forEach(product => {
          if (!product.canonicalName || !product.displayName || !product.brand) return;
          
          const category = product.brand === 'Good Vibes Pocket' ? 'Good Vibes Pocket 150mg' :
                          product.brand === 'Good Vibes' ? 'Good Vibes 500mg' :
                          product.brand === 'Good Vibes Rosin' ? 'Good Vibes Rosin' :
                          product.brand === 'Good Vibes X' ? 'Good Vibes X 1000mg' :
                          product.brand === 'Good Vibes +' ? 'Good Vibes Plus' :
                          product.brand === 'Good Feels' ? 'Good Feels 150mg' : null;
          
          if (category && grouped[category]) {
            grouped[category].push({
              canonicalName: product.canonicalName,
              displayName: product.displayName,
              thc: product.thc,
              brand: product.brand
            });
          }
        });

        // Sort products within each category
        Object.keys(grouped).forEach(category => {
          grouped[category].sort((a, b) => a.displayName.localeCompare(b.displayName));
        });

        setCanonicalProducts(grouped);
      } catch (error) {
        console.error('Error loading canonical products:', error);
      }
    };

    fetchAndGroupProducts();
  }, []);
  
  // Filter products based on search term
  useEffect(() => {
    if (!searchTerm.trim() || !canonicalProducts || Object.keys(canonicalProducts).length === 0) {
      setFilteredProducts(canonicalProducts);
      return;
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    const filtered = {};
    
    Object.entries(canonicalProducts).forEach(([category, products]) => {
      const matchedProducts = products.filter(product => 
        product.canonicalName.toLowerCase().includes(searchTermLower) || 
        product.displayName.toLowerCase().includes(searchTermLower) ||
        (product.thc && product.thc.toLowerCase().includes(searchTermLower))
      );
      
      if (matchedProducts.length > 0) {
        filtered[category] = matchedProducts;
      }
    });
    
    setFilteredProducts(filtered);
  }, [searchTerm, canonicalProducts]);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus search input when dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    } else {
      // Clear search when dropdown closes
      setSearchTerm('');
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen(!isOpen);
    }
  }, [isOpen]);

  const handleOptionClick = useCallback((product) => {
    setSelectedProducts(prevSelected => {
      const isAlreadySelected = prevSelected.some(p => p.canonicalName === product.canonicalName);
      let newSelected;
      if (isAlreadySelected) {
        newSelected = prevSelected.filter(p => p.canonicalName !== product.canonicalName);
      } else {
        newSelected = [...prevSelected, product];
      }
      // Call onChange and onSearch with the new selection
      if (newSelected.length === 0) {
        onChange('');
        onSearch([]);
      } else {
        const searchTerms = newSelected.map(p => p.canonicalName);
        onChange(searchTerms.join(' | '));
        onSearch(searchTerms);
      }
      return newSelected;
    });
  }, [onChange, onSearch]);

  const handleShowAll = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Show All clicked - clearing all selections');
    }
    setSelectedProducts([]);
    onChange('');
    onSearch([]); // Pass empty array to clear all filters
    setIsOpen(false);
  }, [onChange, onSearch]);

  const handleClearSelection = useCallback(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Clear selection clicked');
    }
    setSelectedProducts([]);
    onChange('');
    onSearch([]); // Pass empty array to clear all filters
  }, [onChange, onSearch]);

  const getDisplayText = () => {
    if (selectedProducts.length === 0) {
      return 'Select products...';
    } else if (selectedProducts.length === 1) {
      return selectedProducts[0].displayName;
    } else {
      return `${selectedProducts.length} products selected`;
    }
  };

  return (
    <div className="product-dropdown-container" ref={dropdownRef}>
      <div className="product-dropdown-wrapper">
        <button
          type="button"
          className={`product-dropdown-toggle ${isOpen ? 'open' : ''}`}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className="dropdown-text">{getDisplayText()}</span>
          <span className="dropdown-arrow">
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
              <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </button>

        {selectedProducts.length > 0 && (
          <button
            type="button"
            className="clear-selection-btn"
            onClick={handleClearSelection}
            title="Clear selection"
          >
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <div className="product-dropdown-menu">
          <div className="dropdown-header">
            <div className="search-container">
              <input
                type="text"
                className="product-search-input"
                name="store-locator-product-search"
                placeholder="Search products..."
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore="true"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                ref={searchInputRef}
                style={{
                  backgroundImage: 'none',
                  backgroundRepeat: 'no-repeat',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
              />
              {searchTerm && (
                <button 
                  className="clear-search-btn"
                  onClick={() => setSearchTerm('')}
                >
                  ×
                </button>
              )}
            </div>
            <button
              type="button"
              className="show-all-btn"
              onClick={handleShowAll}
            >
              Show All Products
            </button>
          </div>

          <div className="dropdown-content">
            {Object.entries(filteredProducts).map(([category, products]) => (
              products.length > 0 && (
                <div key={category} className="product-category">
                  <div className="category-header">{category}</div>
                  <div className="category-products">
                    {products.map((product) => {
                      const isSelected = selectedProducts.some(p => p.canonicalName === product.canonicalName);
                      return (
                        <div
                          key={product.canonicalName}
                          className={`product-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleOptionClick(product)}
                        >
                          <div className="product-option-content">
                            <span className="product-name">{product.displayName}</span>
                            <span className="product-thc">{product.thc}</span>
                          </div>
                          {isSelected && (
                            <span className="selected-indicator">✓</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

ProductDropdown.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  selectedCanonicalNames: PropTypes.array
};

export default ProductDropdown;
